// client/src/components/VisitLogPixel.jsx
import React, {useContext, useEffect, useRef} from 'react';
import { useLocation } from 'react-router-dom';
import { collectVisitLogPublic } from '../http/userLogAPI';
import {Context} from "../index";

/** SHA-256 → hex (с безопасным фолбэком) */
async function sha256Hex(input) {
    try {
        if (window.crypto?.subtle) {
            const enc = new TextEncoder();
            const buf = await crypto.subtle.digest('SHA-256', enc.encode(input));
            return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
        }
    } catch (_) { /* ignore */ }
    // Фолбэк: простой нер крипто-хэш (достаточно для стабильного id, не для безопасности)
    let h = 0;
    const s = String(input);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return ('00000000' + (h >>> 0).toString(16)).slice(-8);
}

/** UTM-параметры */
function getUtmParams(search) {
    const p = new URLSearchParams(search || '');
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'].forEach(k => {
        const v = p.get(k);
        if (v) utm[k] = v;
    });
    return Object.keys(utm).length ? utm : null;
}

/** стабильный анонимный SID в localStorage */
function getAnonSid() {
    try {
        let sid = localStorage.getItem('__anon_sid__');
        if (!sid) {
            sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem('__anon_sid__', sid);
        }
        return sid;
    } catch {
        return null;
    }
}

/** request_id на событие */
function genRequestId() {
    const rnd = Math.random().toString(36).slice(2);
    const ts = Date.now().toString(36);
    return `${ts}-${rnd}`;
}

/** UA разбор (UA-CH приоритетно, затем классический UA) */
function parseUA(user_agent) {
    const res = {
        ua_browser: null,
        ua_browser_version: null,
        ua_os: null,
        ua_os_version: null,
        ua_device_type: null,
        ua_brand: null,
        ua_model: null,
    };

    const uad = navigator.userAgentData || navigator.userAgentData?.brands ? navigator.userAgentData : null;

    // UA-CH (современный способ)
    if (navigator.userAgentData) {
        try {
            const brands = navigator.userAgentData.brands || navigator.userAgentData.uaList || [];
            const brandObj = brands.find(b => /Chrom(e|ium)|Firefox|Safari|Edge|Opera|Brave|Vivaldi/i.test(b.brand)) || brands[0];
            if (brandObj) {
                res.ua_browser = brandObj.brand;
                res.ua_browser_version = brandObj.version || null;
            }
            res.ua_device_type = navigator.userAgentData.mobile ? 'mobile' : 'desktop';
            // Расширенные UA-CH (может требовать secure context / разрешения)
            // Попробуем best-effort: platform/arch/model/bitness
            // Эти поля могут быть undefined — это ок, мы просто не перезапишем.
            // @ts-ignore
            res.ua_brand = res.ua_browser; // как минимум бренд движка/браузера
            // @ts-ignore
            if (navigator.userAgentData.platform) res.ua_os = navigator.userAgentData.platform;
        } catch (_) { /* ignore */ }
    }

    // Классический UA fallback (регексы)
    if (!res.ua_browser || !res.ua_browser_version) {
        const ua = user_agent || '';
        let m;
        // Порядок важен
        if ((m = ua.match(/Edg\/([\d.]+)/))) { res.ua_browser = 'Edge'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/OPR\/([\d.]+)/))) { res.ua_browser = 'Opera'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Vivaldi\/([\d.]+)/))) { res.ua_browser = 'Vivaldi'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Brave\/([\d.]+)/))) { res.ua_browser = 'Brave'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Chrome\/([\d.]+)/))) { res.ua_browser = 'Chrome'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Firefox\/([\d.]+)/))) { res.ua_browser = 'Firefox'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Version\/([\d.]+).*Safari/))) { res.ua_browser = 'Safari'; res.ua_browser_version = m[1]; }
        else if ((m = ua.match(/Safari\/([\d.]+)/))) { res.ua_browser = 'Safari'; res.ua_browser_version = m[1]; }

        if (!res.ua_device_type) {
            if (/Mobi|Android/i.test(ua)) res.ua_device_type = 'mobile';
            else if (/iPad|Tablet/i.test(ua)) res.ua_device_type = 'tablet';
            else res.ua_device_type = 'desktop';
        }

        if (!res.ua_os) {
            if (/Windows NT 10\.0/.test(ua)) { res.ua_os = 'Windows'; res.ua_os_version = '10/11'; }
            else if (/Windows NT 6\.3/.test(ua)) { res.ua_os = 'Windows'; res.ua_os_version = '8.1'; }
            else if (/Windows NT 6\.2/.test(ua)) { res.ua_os = 'Windows'; res.ua_os_version = '8'; }
            else if (/Windows NT 6\.1/.test(ua)) { res.ua_os = 'Windows'; res.ua_os_version = '7'; }
            else if (/Mac OS X ([\d_]+)/.test(ua)) { res.ua_os = 'macOS'; res.ua_os_version = ua.match(/Mac OS X ([\d_]+)/)[1].replace(/_/g,'.'); }
            else if (/Android ([\d.]+)/.test(ua)) { res.ua_os = 'Android'; res.ua_os_version = ua.match(/Android ([\d.]+)/)[1]; }
            else if (/iPhone OS ([\d_]+)/.test(ua)) { res.ua_os = 'iOS'; res.ua_os_version = ua.match(/iPhone OS ([\d_]+)/)[1].replace(/_/g,'.'); }
            else if (/Linux/.test(ua)) { res.ua_os = 'Linux'; res.ua_os_version = null; }
        }
    }

    // Модель/бренд устройства (грубый эвристический fallback для мобилок)
    if (!res.ua_model && /Android.*;.*Build\/([^)]+)\)/i.test(user_agent || '')) {
        // часто в UA есть что-то вроде ...; Pixel 7 Pro Build/TQ3A.230805.001...
        const mm = (user_agent || '').match(/Android.*;\s*([^;()]+?)\s*Build\//i);
        if (mm) res.ua_model = mm[1].trim();
    }

    return res;
}

/** Эвристика headless */
function detectHeadless() {
    try {
        const isWD = !!navigator.webdriver;
        const noPlugins = navigator.plugins && navigator.plugins.length === 0;
        const languagesOk = Array.isArray(navigator.languages) && navigator.languages.length > 0;
        return isWD || (noPlugins && !languagesOk) ? true : false;
    } catch {
        return null;
    }
}

/** Canvas hash */
async function getCanvasHash() {
    try {
        const c = document.createElement('canvas');
        const ctx = c.getContext('2d');
        c.width = 240; c.height = 60;
        ctx.textBaseline = 'top';
        ctx.font = '16px "Arial"';
        ctx.fillStyle = '#f60';
        ctx.fillRect(0, 0, 240, 60);
        ctx.fillStyle = '#069';
        ctx.fillText('Virtalibry FP Canvas 🧪', 2, 2);
        ctx.strokeStyle = '#fff';
        ctx.strokeRect(1, 1, 238, 58);
        const data = c.toDataURL();
        return await sha256Hex(data);
    } catch { return null; }
}

/** WebGL hash */
async function getWebglHash() {
    try {
        const c = document.createElement('canvas');
        const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
        if (!gl) return null;
        const info = {
            aliasedLineWidthRange: gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE),
            aliasedPointSizeRange: gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE),
            alphaBits: gl.getParameter(gl.ALPHA_BITS),
            depthBits: gl.getParameter(gl.DEPTH_BITS),
            stencilBits: gl.getParameter(gl.STENCIL_BITS),
            antialias: gl.getContextAttributes().antialias,
            maxCombinedTextureImageUnits: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
            maxCubeMapTextureSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
            maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
            maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
            maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
            maxVaryingVectors: gl.getParameter(gl.MAX_VARYING_VECTORS),
            maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
            maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
            maxVertexUniformVectors: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
            vendor: null,
            renderer: null
        };
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
            info.vendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL);
            info.renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
        }
        return await sha256Hex(JSON.stringify(info));
    } catch { return null; }
}

/** Audio hash */
async function getAudioHash() {
    try {
        const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);
        const osc = ctx.createOscillator();
        const comp = ctx.createDynamicsCompressor();
        osc.type = 'triangle';
        osc.frequency.value = 1000;
        osc.connect(comp);
        comp.connect(ctx.destination);
        osc.start(0);
        const buf = await ctx.startRendering();
        let sum = 0;
        const ch = buf.getChannelData(0);
        for (let i = 0; i < ch.length; i += 100) sum += Math.abs(ch[i]);
        return await sha256Hex('aud-' + sum.toFixed(6));
    } catch { return null; }
}

/** MediaDevices hash (без запроса доступа к камере/микрофону) */
async function getMediaDevicesHash() {
    try {
        if (!navigator.mediaDevices?.enumerateDevices) return null;
        const list = await navigator.mediaDevices.enumerateDevices();
        const compact = list.map(d => ({ kind: d.kind, label: d.label ? d.label.replace(/\s+/g,' ').trim() : '', groupId: !!d.groupId }));
        return await sha256Hex(JSON.stringify(compact));
    } catch { return null; }
}

/** Battery info hash */
async function getBatteryInfoHash() {
    try {
        if (!navigator.getBattery) return null;
        const b = await navigator.getBattery();
        const info = {
            ch: b.charging,
            lv: b.level,
            dt: b.dischargingTime,
            ct: b.chargingTime
        };
        return await sha256Hex(JSON.stringify(info));
    } catch { return null; }
}

/** Источник отпечатка (как было) */
function buildFingerprintSource() {
    const screen_width = window.screen?.width ?? null;
    const screen_height = window.screen?.height ?? null;
    const viewport_width = window.innerWidth ?? null;
    const viewport_height = window.innerHeight ?? null;
    const pixel_ratio = window.devicePixelRatio ?? null;

    const language = navigator.language || null;
    const languages = Array.isArray(navigator.languages) ? navigator.languages : null;
    const platform = navigator.platform || null;
    const timezone_offset = new Date().getTimezoneOffset();
    const user_agent = navigator.userAgent || null;

    return JSON.stringify({
        ua: user_agent,
        scr: [screen_width, screen_height, pixel_ratio],
        vp: [viewport_width, viewport_height],
        lang: [language, languages],
        plat: platform,
        tz: timezone_offset,
        sid: getAnonSid(),
    });
}

export default function VisitLogPixel() {
    const location = useLocation();
    const lastSentRef = useRef({ key: '', ts: 0 });
    const { user } = useContext(Context);

    useEffect(() => {
        let aborted = false;

        (async () => {
            try {
                const path = location.pathname + (location.search || '');
                const key = path;
                const now = Date.now();

                // троттлинг/дедупликация: не слать чаще 1 раза на тот же путь в 5 сек
                if (lastSentRef.current.key === key && (now - lastSentRef.current.ts) < 5000) return;
                lastSentRef.current = { key, ts: now };

                // первый заход на сессию — запомним landing
                if (!sessionStorage.getItem('__landing_path__')) {
                    sessionStorage.setItem('__landing_path__', path);
                }

                // базовые данные
                const screen_width = window.screen?.width ?? null;
                const screen_height = window.screen?.height ?? null;
                const viewport_width = window.innerWidth ?? null;
                const viewport_height = window.innerHeight ?? null;
                const pixel_ratio = window.devicePixelRatio ?? null;

                const language = navigator.language || null;
                const languages = Array.isArray(navigator.languages) ? navigator.languages : null;
                const platform = navigator.platform || null;
                const timezone_offset = new Date().getTimezoneOffset();
                const user_agent = navigator.userAgent || null;

                const referrer = document.referrer || null;
                const landing_path = sessionStorage.getItem('__landing_path__') || path;
                const utm = getUtmParams(location.search);

                const fpSource = buildFingerprintSource();
                const fp_hash = await sha256Hex(fpSource);

                // грубая латентность навигации
                const nav = performance.getEntriesByType?.('navigation')?.[0];
                const latency_ms = nav ? Math.round(nav.duration) : null;

                // UA разбор + headless
                const uaParsed = parseUA(user_agent);
                const is_headless = detectHeadless();

                // Доп. отпечатки in parallel
                const [
                    canvas_hash,
                    webgl_hash,
                    audio_hash,
                    media_devices_hash,
                    battery_info_hash
                ] = await Promise.all([
                    getCanvasHash(),
                    getWebglHash(),
                    getAudioHash(),
                    getMediaDevicesHash(),
                    getBatteryInfoHash()
                ]);

                // UA-CH Client Hints (расширим client_hints)
                const ch = {
                    device_memory: navigator.deviceMemory ?? null,
                    hardware_concurrency: navigator.hardwareConcurrency ?? null,
                    brands: null,
                    mobile: null,
                    platform: null,
                    platformVersion: null,
                    architecture: null,
                    model: null,
                    bitness: null,
                };
                try {
                    // Не все браузеры поддерживают getHighEntropyValues; best-effort
                    if (navigator.userAgentData?.getHighEntropyValues) {
                        const hi = await navigator.userAgentData.getHighEntropyValues(
                            ['platform', 'platformVersion', 'architecture', 'model', 'uaFullVersion', 'bitness']
                        );
                        ch.platform = hi.platform ?? null;
                        ch.platformVersion = hi.platformVersion ?? null;
                        ch.architecture = hi.architecture ?? null;
                        ch.model = hi.model ?? null;
                        ch.bitness = hi.bitness ?? null;
                    }
                    if (navigator.userAgentData) {
                        ch.brands = navigator.userAgentData.brands || null;
                        ch.mobile = !!navigator.userAgentData.mobile;
                    }
                } catch { /* ignore */ }

                const payload = {
                    userId: user?.user?.id,

                    // session/request identifiers
                    session_id: getAnonSid(),
                    request_id: genRequestId(),

                    event_type: 'pageview',
                    action: 'route_change',
                    result: 'success',

                    // экран/локаль
                    screen_width,
                    screen_height,
                    viewport_width,
                    viewport_height,
                    pixel_ratio,
                    language,
                    languages,
                    platform,
                    timezone_offset,

                    // UA
                    user_agent,
                    ua_browser: uaParsed.ua_browser,
                    ua_browser_version: uaParsed.ua_browser_version,
                    ua_os: uaParsed.ua_os,
                    ua_os_version: uaParsed.ua_os_version,
                    ua_device_type: uaParsed.ua_device_type,
                    ua_brand: uaParsed.ua_brand,
                    ua_model: uaParsed.ua_model,
                    is_headless,

                    // отпечатки
                    fp_hash,
                    canvas_hash,
                    webgl_hash,
                    audio_hash,
                    // fonts_hash: оставим на будущее — корректная детекция шрифтов без внешних либ ненадёжна
                    fonts_hash: null,
                    media_devices_hash,
                    battery_info_hash,

                    // источники
                    referrer,
                    utm,
                    landing_path,

                    // клиентский http-контекст
                    method: 'GET',
                    path,
                    query: Object.fromEntries(new URLSearchParams(location.search || '')),
                    status_code: null,   // со стороны клиента узнать нельзя
                    latency_ms,
                    req_size: null,      // в браузере узнать нельзя надёжно
                    res_size: null,      // в браузере узнать нельзя надёжно

                    // client hints
                    client_hints: ch,

                    risk_score: null,
                    notes: null,
                    extra: null,
                };

                if (!aborted) {
                    await collectVisitLogPublic(payload); // 👈 api/userlogs/collect
                }
            } catch {
                // пиксель не должен ломать приложение
            }
        })();

        return () => { aborted = true; };
    }, [location.pathname, location.search]);

    return null;
}
